// Small, factual helper shared by the home result card and the share-text builders: the one-line blurb
// that goes under the NetRiskScan Index.
//
// This module used to also own buildHomeSummary — a whole verdict object (title/tone/detail sentence/
// facts/suggestions) that powered the home page's "Risk Summary" card: a paragraph of risk
// explanation, three bullet "suggested use" lines, and a data-source note. That card is gone (deleted,
// not collapsed — see HomeReportCard.tsx), and so is the reasoning that built its copy.
//
// network-identity-semantic-v2: this module used to also own ipAttributeLabel/networkTypeLabel — two
// independent, occasionally-disagreeing readings of "what kind of network is this" (one read
// risk.networkClassification, the other only raw flags). Both are gone; every caller now reads
// resolveNetworkIdentityPresentation() in networkIdentity.ts instead, the single place that answers
// the Access/Infrastructure/Connection axes.
import { homeShellString, type getStrings } from "../i18n/strings";

type Strings = ReturnType<typeof getStrings>;

/// The one short line under the NetRiskScan Index (spec: no paragraph, no operational advice — just a
/// plain-language reading of the band a visitor already saw as a number and a word). Keyed by the same
/// five-band vocabulary as indexBandNames, so this sentence and that badge can never disagree.
const NET_RISK_SCAN_BLURB_KEYS = {
  excellent: "homeNetRiskScanBlurbExcellent",
  good: "homeNetRiskScanBlurbGood",
  fair: "homeNetRiskScanBlurbFair",
  poor: "homeNetRiskScanBlurbPoor",
  high_risk: "homeNetRiskScanBlurbHighRisk",
} as const;

export function netRiskScanBlurb(level: string | null, strings: Strings): string {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);
  const key = level ? NET_RISK_SCAN_BLURB_KEYS[level as keyof typeof NET_RISK_SCAN_BLURB_KEYS] : undefined;
  return t(key ?? "homeNetRiskScanBlurbUnknown");
}
